/**
 * Atlas shell + role gate — Phase 1 (Slice 1a).
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────┐
 *   │ AtlasHeader (logo + breadcrumb + profile menu)  │
 *   ├───────────┬─────────────────────────────────────┤
 *   │ AtlasNav  │  <Outlet />                         │
 *   │ (sidebar) │                                     │
 *   └───────────┴─────────────────────────────────────┘
 *
 * Guards:
 *   - Not logged in     → Redirect via <Protected/> wrapper (in App.js).
 *   - Not an advisor    → Redirect to "/" with a toast.
 *   - Cert reviewer     → Redirect to "/" with a toast (never sees Atlas).
 *
 * Toaster is mounted here (Atlas-scoped) so success/failure feedback is
 * visible even though the global App.js Toaster is intentionally
 * unmounted for the traveler surface.
 *
 * Msg 555 · Mobile responsive — below `md` (768px), the sidebar collapses
 * into a slide-in drawer opened via a hamburger button in AtlasHeader.
 * Tapping the backdrop or a nav item inside the drawer closes it. On
 * desktop (`>= md`) the sidebar is static (unchanged behavior).
 */
import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AtlasNav from "@/components/atlas/AtlasNav";
import AtlasHeader from "@/components/atlas/AtlasHeader";
import { subscribeVersion, getLiveVersion } from "@/lib/versionCheck";

export default function AtlasLayout() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();

  const roles = user?.roles || [];
  const isCertReviewer = roles.includes("cert_reviewer");
  // (2026-08-04, P0 prod fix) — accept legacy scalar `role: "admin"` as
  // an Atlas admin/advisor equivalent. Older accounts on production
  // pre-date the `roles: ["atlas_admin", ...]` migration (server.py
  // §Atlas Phase 2 · Slice 2b re-seed) and would otherwise be
  // silently redirected off `/atlas/*` because the sonner Toaster is
  // unmounted app-wide, so the "Atlas access required" message never
  // surfaces to the user. Belt-and-braces: `role === "admin"` (the
  // legacy singular field that AdminProtected in App.js still
  // honors) also unlocks Atlas.
  const isLegacyAdmin = user?.role === "admin";
  const isAdvisor = (roles.includes("advisor") || isLegacyAdmin) && !isCertReviewer;
  const isAdmin = (roles.includes("atlas_admin") || isLegacyAdmin) && !isCertReviewer;

  // Msg 555 — mobile drawer state. Closed by default; auto-closes on
  // route change so tapping a nav item inside the drawer navigates AND
  // dismisses the overlay.
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => { setDrawerOpen(false); }, [pathname]);
  // Prevent background body scroll while the drawer is open on mobile.
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  // Msg 578 — live backend version badge (falls back to the baked
  // REACT_APP_BUILD_VERSION, then to "dev").
  const [liveVersion, setLiveVersion] = useState(getLiveVersion());
  useEffect(() => subscribeVersion((v) => setLiveVersion(v)), []);

  React.useEffect(() => {
    if (!user) return;
    if (isCertReviewer) {
      toast("Atlas is unavailable for certification reviewer accounts.");
      nav("/", { replace: true });
      return;
    }
    if (!isAdvisor) {
      toast("Atlas access required — request access from your profile.");
      nav("/", { replace: true });
    }
  }, [user, isAdvisor, isCertReviewer, nav]);

  if (!user || !isAdvisor) return null;

  return (
    <div
      className="min-h-screen bg-white flex flex-col"
      data-testid="atlas-shell"
    >
      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          style: {
            // Msg 581 · K.1 — kill italic-looking script font on toasts.
            // Switched from Kalam (handwritten/script) to Outfit — matches
            // the rest of the Atlas UI shell (`.font-hand` post-cleanup).
            fontFamily: 'Outfit, system-ui, sans-serif',
            fontStyle: 'normal',
          },
        }}
      />
      <AtlasHeader
        isAdmin={isAdmin}
        onOpenDrawer={() => setDrawerOpen(true)}
      />
      <div className="flex-1 min-h-0 flex">
        {/* Desktop sidebar (md+): static column, unchanged from prior. */}
        <aside
          className="hidden md:flex w-60 lg:w-64 border-r border-tb-navy/15 bg-white flex-col shrink-0"
          data-testid="atlas-sidebar"
        >
          <AtlasNav isAdmin={isAdmin} />
        </aside>

        {/* Mobile drawer (< md) — backdrop + slide-in aside. Only mounts
            when open so it doesn't intercept touches at rest. */}
        {drawerOpen && (
          <>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label="Close navigation"
              className="md:hidden fixed inset-0 z-40 bg-black/40"
              data-testid="atlas-drawer-backdrop"
            />
            <aside
              className="md:hidden fixed left-0 top-0 bottom-0 z-50 w-4/5 max-w-[320px] bg-white border-r border-tb-navy/15 flex flex-col shadow-2xl"
              data-testid="atlas-drawer"
              role="dialog"
              aria-label="Atlas navigation"
            >
              <div className="shrink-0 h-14 px-4 flex items-center justify-between border-b border-tb-navy/10">
                <span
                  className="font-hand text-[11px] uppercase tracking-widest text-tb-navy/60"
                >
                  Advisor Atlas
                </span>
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Close menu"
                  data-testid="atlas-drawer-close"
                  className="h-9 w-9 -mr-2 inline-flex items-center justify-center rounded-full text-tb-navy hover:bg-tb-navy/5"
                >
                  <span className="text-2xl leading-none">×</span>
                </button>
              </div>
              <AtlasNav
                isAdmin={isAdmin}
                onNavigate={() => setDrawerOpen(false)}
              />
            </aside>
          </>
        )}

        <main
          className="flex-1 min-w-0 min-h-0 overflow-y-auto bg-white"
          data-testid="atlas-main"
        >
          <Outlet />
        </main>
      </div>
      {/* Msg 578 · visible build-version badge so the user (and support)
          can tell at a glance which bundle is loaded in the browser.
          Prefers the live `/api/health/version` value emitted by
          versionCheck.js; falls back to the baked REACT_APP_BUILD_VERSION,
          then to "dev". */}
      <div
        data-testid="atlas-build-version"
        className="fixed bottom-2 right-3 z-30 pointer-events-none select-none font-mono text-[10px] text-tb-navy/30"
      >
        v{liveVersion || process.env.REACT_APP_BUILD_VERSION || "dev"}
      </div>
    </div>
  );
}
